/**
 * Guide handling: turn an alignment or a drawn polyline into evenly spaced
 * stations with smoothed tangents.
 *
 * Only XY is used. The guide's elevation is irrelevant — it exists to supply a
 * station axis and a section normal, nothing else (spec §4).
 */
import type { Alignment } from '@/core/alignment';

import type { Guide, GuideStation } from './types';

/** Drop Z from an alignment's flattened XYZ. */
export function guideFromAlignment(a: Alignment): Guide {
  const n = a.points.length / 3;
  const out = new Float64Array(n * 2);
  for (let i = 0; i < n; i++) {
    out[i * 2] = a.points[i * 3];
    out[i * 2 + 1] = a.points[i * 3 + 1];
  }
  return out;
}

export function guideFromXY(pts: ReadonlyArray<readonly [number, number]>): Guide {
  const out = new Float64Array(pts.length * 2);
  for (let i = 0; i < pts.length; i++) {
    out[i * 2] = pts[i][0];
    out[i * 2 + 1] = pts[i][1];
  }
  return out;
}

/**
 * Resample the guide at `interval` and attach a tangent to each station.
 *
 * The tangent is a CENTRAL difference over the resampled points, not the
 * containing segment's direction. A coarse guide (a 5-vertex sketch) otherwise
 * produces piecewise-constant normals that jump at each vertex, which puts a
 * visible kink in the extracted crest.
 */
export function guideStations(guide: Guide, interval: number): GuideStation[] {
  const n = guide.length / 2;
  if (n < 2 || !(interval > 0)) return [];

  const cum = new Float64Array(n);
  for (let i = 1; i < n; i++) {
    const dx = guide[i * 2] - guide[(i - 1) * 2];
    const dy = guide[i * 2 + 1] - guide[(i - 1) * 2 + 1];
    cum[i] = cum[i - 1] + Math.hypot(dx, dy);
  }
  const total = cum[n - 1];
  if (total < interval) return [];

  const stations: number[] = [];
  for (let s = 0; s < total; s += interval) stations.push(s);
  if (total - stations[stations.length - 1] > 1e-9) stations.push(total);

  // Resample XY.
  const xs = new Float64Array(stations.length);
  const ys = new Float64Array(stations.length);
  let seg = 1;
  for (let k = 0; k < stations.length; k++) {
    const s = stations[k];
    while (seg < n - 1 && cum[seg] < s) seg++;
    const s0 = cum[seg - 1];
    const s1 = cum[seg];
    const t = s1 > s0 ? (s - s0) / (s1 - s0) : 0;
    xs[k] = guide[(seg - 1) * 2] + t * (guide[seg * 2] - guide[(seg - 1) * 2]);
    ys[k] = guide[(seg - 1) * 2 + 1] + t * (guide[seg * 2 + 1] - guide[(seg - 1) * 2 + 1]);
  }

  // Central-difference tangents, one-sided at the ends.
  const out: GuideStation[] = [];
  for (let k = 0; k < stations.length; k++) {
    const a = Math.max(k - 1, 0);
    const b = Math.min(k + 1, stations.length - 1);
    let tx = xs[b] - xs[a];
    let ty = ys[b] - ys[a];
    const len = Math.hypot(tx, ty);
    if (len > 0) {
      tx /= len;
      ty /= len;
    } else {
      tx = 1;
      ty = 0;
    }
    out.push({ x: xs[k], y: ys[k], tx, ty, station: stations[k] });
  }
  return out;
}
